import React from 'react';

/**
 * Badge — a small status/category/priority pill.
 *
 * COMPONENT RULE (enforce in review):
 *   Use a Badge ONLY for a status, category, or priority — a value drawn from a small,
 *   meaningful set that maps to a semantic color. NEVER use a badge for an identifier
 *   (order id, mold code, part number) or free-form data (customer name, assignee). Those
 *   are plain text. "Everything is a pill" destroys visual hierarchy — reserve the filled
 *   shape for the few things a user scans for.
 *
 * Variants map to intent, not to a raw color, so the palette can change in one place.
 */
export type BadgeVariant =
  | 'neutral'
  | 'info'
  | 'success'
  | 'warning'
  | 'danger';

const VARIANT_STYLES: Record<BadgeVariant, string> = {
  neutral: 'bg-gray-100 text-gray-600',
  info: 'bg-blue-50 text-blue-700',
  success: 'bg-green-50 text-green-700',
  warning: 'bg-amber-50 text-amber-700',
  danger: 'bg-red-50 text-red-700',
};

interface BadgeProps {
  variant?: BadgeVariant;
  className?: string;
  children: React.ReactNode;
}

const Badge: React.FC<BadgeProps> = ({
  variant = 'neutral',
  className = '',
  children,
}) => (
  <span
    className={`inline-flex w-fit items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${VARIANT_STYLES[variant]} ${className}`}
  >
    {children}
  </span>
);

export default Badge;
